import { css, CSSObject } from '@emotion/react';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Box, IconButton, List, ListItemButton, Stack, Typography } from '@mui/material';
import { motion } from 'motion/react';
import { useCallback, useMemo, useState } from 'react';
import { colors } from '../../../../../theme/colors';
import { cardStyles } from '../../../../../theme/component-styles';
import { captureUnexpectedState } from '../../../../../util/SentryUtils';
import { SubSection } from '../../../Summary/components/SubSection';
import { NoDataMessage2 } from '../../Messages/HelpMessage';
import { useTextualKpiData } from '../hooks/useFinancialsGridData';

const TextualKpisContainerCss = css`
  margin-top: 0.5rem;
  display: grid;
  grid-template-columns: 14rem 1fr;
  height: 100%;
  max-height: 100%;
  gap: 0.5rem;
`;

const ColumnCss = css({
  height: '100%',
  maxHeight: '100%',
  overflowY: 'auto',
  padding: 0,
});

const PeriodButtonListCss = css([
  ColumnCss,
  { display: 'grid', alignContent: 'start', gap: '0.5rem', paddingRight: '0.5rem', paddingBottom: '1rem' },
]);

const PeriodButtonCss = css({
  ...cardStyles,
  fontSize: '0.875rem',
  '&.Mui-selected': {
    background: `${colors.primary[10]}99`,
  },
});

const KpiSectionCss = css([
  cardStyles as CSSObject,
  ColumnCss,
  { position: 'relative', paddingRight: '0', marginRight: '2rem' },
]);

const ToggleExpandAllContainerCss = css`
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.5rem;
`;

export const allPeriodsLabel = 'All periods';

export function TextualKpis() {
  const { allPeriods, textualKpisByDate } = useTextualKpiData() ?? {};
  const [expandAll, setExpandAll] = useState(true);

  const toggleExpandAll = useCallback(() => {
    setExpandAll((prev) => !prev);
  }, []);

  const { selectedPeriods, togglePeriods } = useSelectedPeriods(allPeriods);

  const periodButtons = useMemo(() => {
    if (!allPeriods) {
      return null;
    }
    return [[allPeriodsLabel, allPeriodsLabel], ...allPeriods.entries()].map(([period, formattedPeriod]) => {
      const isSelected =
        selectedPeriods.size === allPeriods.size ? period === allPeriodsLabel : selectedPeriods.has(period);
      return (
        <PeriodButton
          key={period}
          selected={isSelected}
          onClick={() => togglePeriods(period)}
          label={formattedPeriod}
        />
      );
    });
  }, [allPeriods, selectedPeriods, togglePeriods]);

  const kpis = useMemo(() => {
    if (!selectedPeriods || !textualKpisByDate) {
      return null;
    }

    let kpisByNameAndDate = new Map<string, Map<string, string>>();

    selectedPeriods.forEach((period) => {
      const kpis = textualKpisByDate.get(period);
      if (!kpis?.size) {
        return;
      }
      kpis.entries().forEach(([kpiName, value]) => {
        const existing = kpisByNameAndDate.get(kpiName);
        if (!existing) {
          kpisByNameAndDate.set(kpiName, new Map([[period, value]]));
        } else {
          existing.set(period, value);
        }
      });
    });

    kpisByNameAndDate = new Map(
      [...kpisByNameAndDate.entries()].sort((e1, e2) => e1[0].localeCompare(e2[0]))
    );

    if (!kpisByNameAndDate || kpisByNameAndDate.size === 0 || !allPeriods) {
      return <NoDataMessage2 />;
    }
    return (
      <Stack p='1rem' gap='1rem' height='100%' maxHeight={'100%'} overflow='auto'>
        <motion.div layout transition={{ duration: 0.2 }}>
          {Array.from(kpisByNameAndDate.entries()).map(([kpiName, valuesByDate]) => {
            return (
              <KpiSection
                key={kpiName}
                kpiName={kpiName}
                valuesByDate={valuesByDate}
                selectedPeriods={selectedPeriods}
                allPeriods={allPeriods}
                expandAll={expandAll}
              />
            );
          })}
        </motion.div>
      </Stack>
    );
  }, [allPeriods, expandAll, selectedPeriods, textualKpisByDate]);

  if (!allPeriods || !textualKpisByDate) {
    return null;
  }

  return (
    <div css={TextualKpisContainerCss}>
      <Stack gap='0.5rem'>
        <List css={PeriodButtonListCss}>{periodButtons}</List>
      </Stack>
      <div css={KpiSectionCss}>
        <ExpandAllButton onClick={toggleExpandAll} expanded={expandAll} />
        {kpis}
      </div>
    </div>
  );
}

interface IPeriodButtonProps {
  label: string;
  onClick: () => void;
  selected: boolean;
}
function PeriodButton({ selected, onClick, label }: IPeriodButtonProps) {
  return (
    <li>
      <ListItemButton css={PeriodButtonCss} selected={selected} onClick={onClick} aria-selected={selected}>
        {label}
      </ListItemButton>
    </li>
  );
}

function ExpandAllButton({ onClick, expanded }: { onClick: () => void; expanded: boolean }) {
  return (
    <motion.div key={String(expanded)} animate={{ opacity: [0.5, 1] }} transition={{ duration: 0.2 }}>
      <IconButton
        color='secondary'
        onClick={onClick}
        css={ToggleExpandAllContainerCss}
        title={expanded ? 'Collapse all' : 'Expand all'}
      >
        {expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
      </IconButton>
    </motion.div>
  );
}

export function useSelectedPeriods(allPeriods: Map<string, string> | undefined) {
  const [selectedPeriods, setSelectedPeriods] = useState<Set<string>>(() => {
    const firstPeriod = [...(allPeriods ?? new Map()).keys()]?.at(0);
    if (!firstPeriod) {
      return new Set();
    }
    return new Set([firstPeriod]);
  });

  const togglePeriod = useCallback(
    (period: string) => {
      if (!allPeriods) {
        captureUnexpectedState('Textual Kpis: no periods', { allPeriods });
        return;
      }
      if (period == allPeriodsLabel) {
        return setSelectedPeriods(new Set([...allPeriods!.keys()]));
      }
      setSelectedPeriods((curr) => {
        if (curr.size == allPeriods.size) {
          return new Set([period]);
        }
        const newSet = new Set(curr);
        if (curr.has(period)) {
          newSet.delete(period);
        } else {
          newSet.add(period);
        }
        return newSet;
      });
    },
    [allPeriods]
  );

  return { selectedPeriods, togglePeriods: togglePeriod };
}

interface IKpiSectionProps {
  kpiName: string;
  valuesByDate: Map<string, string>;
  selectedPeriods: Set<string>;
  allPeriods: Map<string, string>;
  expandAll: boolean;
}
function KpiSection({ kpiName, valuesByDate, selectedPeriods, allPeriods, expandAll }: IKpiSectionProps) {
  return (
    <div key={`${kpiName}-${expandAll}-${[...selectedPeriods]}`}>
      <SubSection
        collapseProps={{ collapsible: true, defaultExpanded: expandAll }}
        title={kpiName}
        style={{ padding: '0', margin: '1rem 0' }}
        noCard
        childrenContainerStyle={{ padding: '0' }}
      >
        {[...valuesByDate.entries()]
          .sort((p1, p2) => p2[0].localeCompare(p1[0])) // sort by period desc
          .map(([period, value]) => {
            return (
              <motion.div key={period} layout transition={{ duration: 0.5 }}>
                <KpiValue
                  showPeriod={selectedPeriods.size > 1}
                  period={allPeriods?.get(period) ?? period}
                  value={value}
                />
              </motion.div>
            );
          })}
      </SubSection>
    </div>
  );
}

interface IKpiValueProps {
  showPeriod: boolean;
  period: string;
  value: string;
}
function KpiValue({ showPeriod, period, value }: IKpiValueProps) {
  return (
    <Box mx={'2rem'} mb='0.5rem'>
      {showPeriod && (
        <Typography
          variant='caption'
          color='text.secondary'
          display={'block'}
          textAlign={'end'}
          width={'100%'}
        >
          {period}
        </Typography>
      )}
      <Typography variant='body2'>{value}</Typography>
    </Box>
  );
}
